import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { forkJoin } from 'rxjs';
import {
  BaseOverlayComponent,
  SignalButton,
  State,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { DataLibraryCollectionFieldResponse } from '@core/api/workspace/models/dataLibraryCollectionFieldResponse';
import type { DataLibraryCollectionItemResponse } from '@core/api/workspace/models/dataLibraryCollectionItemResponse';
import type { GetDataLibraryCollectionApiResponse } from '@core/api/workspace/models/getDataLibraryCollectionApiResponse';
import type { ListDataLibraryCollectionItemsApiResponse } from '@core/api/workspace/models/listDataLibraryCollectionItemsApiResponse';

export interface CollectionDataPreviewSheetData {
  collectionId: string;
  collectionName?: string;
}

interface PreviewColumn {
  key: string;
  label: string;
}

interface PreviewLoadResult {
  collection: GetDataLibraryCollectionApiResponse;
  items: ListDataLibraryCollectionItemsApiResponse;
}

@Component({
  selector: 'app-collection-data-preview-sheet',
  standalone: true,
  imports: [BaseOverlayComponent, SignalButton],
  templateUrl: './collection-data-preview-sheet.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollectionDataPreviewSheetComponent
  extends BaseComponent
  implements OnInit
{
  private readonly dialogRef = inject(DialogRef<null>);
  private readonly dialogData = inject<CollectionDataPreviewSheetData>(
    DIALOG_DATA,
  );
  private readonly workspaceApi = inject(WorkspaceAPIService);

  readonly loadState = new State<PreviewLoadResult>();
  readonly columns = signal<PreviewColumn[]>([]);
  readonly rows = signal<DataLibraryCollectionItemResponse[]>([]);

  readonly title = computed(() => {
    const name = this.dialogData.collectionName?.trim();
    return name ? `Preview — ${name}` : 'Collection data';
  });

  readonly isLoading = computed(() => this.loadState.loading());

  readonly isEmpty = computed(
    () =>
      !this.isLoading() &&
      (this.columns().length === 0 || this.rows().length === 0),
  );

  ngOnInit(): void {
    this.loadPreview();
  }

  cellValue(
    row: DataLibraryCollectionItemResponse,
    columnKey: string,
  ): string {
    const values = row.values as Record<string, unknown>;
    const value = values?.[columnKey];
    if (value === undefined || value === null || value === '') {
      return '—';
    }
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }
    return String(value);
  }

  close(): void {
    this.dialogRef.close(null);
  }

  private loadPreview(): void {
    const collectionId = this.dialogData.collectionId?.trim();
    if (!collectionId) {
      return;
    }

    this.executeRequest({
      state: this.loadState,
      request: forkJoin({
        collection: this.workspaceApi.getDataLibraryCollection(collectionId),
        items: this.workspaceApi.listDataLibraryCollectionItems(collectionId, {
          pageNumber: 1,
          pageSize: 100,
        }),
      }),
      handleError: true,
      onSuccess: (response: PreviewLoadResult) => {
        const fields = response.collection.data?.fields ?? [];
        this.columns.set(
          fields.map(
            (field: DataLibraryCollectionFieldResponse): PreviewColumn => ({
              key: field.key,
              label: field.name || field.key,
            }),
          ),
        );
        this.rows.set(response.items.data?.items ?? []);
      },
    });
  }
}
